import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Image as ImageIcon, Play, Check, AlertTriangle, Copy, Download,
  GitBranch,
} from 'lucide-react';
import { API } from '@/App';
import { StatCard, ExportBtn, ViewHeatmapBtn } from './PhenoScanSubTab';
import ValidationPanel from '@/components/ValidationPanel';

const INPUT_MODES = [
  { key: 'washed', label: 'Washed roots', blurb: 'Dark roots on a bright tray. Otsu thresholding.' },
  { key: 'minirhizotron', label: 'Minirhizotron', blurb: 'Light roots on dark soil (in-situ).' },
  { key: 'manual', label: 'Custom — Manual threshold', blurb: 'Fixed global threshold slider 0–255.' },
  { key: 'adaptive', label: 'Custom — Adaptive (Sauvola)', blurb: 'Local thresholding, handles uneven light.' },
];

const HANDOFF_TARGETS = [
  ['Path Analysis', '/biometrical-analysis', 'path_analysis'],
  ['PCA', '/biometrical-analysis', 'pca'],
  ['D² & Cluster', '/biometrical-analysis', 'd_squared'],
  ['Heritability', '/biometrical-analysis', 'heritability'],
  ['Selection Index', '/biometrical-analysis', 'selection_index'],
  ['AMMI', '/biometrical-analysis', 'ammi'],
  ['GGE Biplot', '/biometrical-analysis', 'gge_biplot'],
];

export default function Root() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mode, setMode] = useState('washed');
  const [manualThreshold, setManualThreshold] = useState(128);
  const [adaptiveWindow, setAdaptiveWindow] = useState(25);
  const [arucoCm, setArucoCm] = useState(3.0);
  const [coinCm, setCoinCm] = useState(2.3);
  const [pruningPx, setPruningPx] = useState(3);
  const [gtLen, setGtLen] = useState('');
  const [gtTips, setGtTips] = useState('');
  const [gtBranches, setGtBranches] = useState('');
  const [gtDia, setGtDia] = useState('');

  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);
  const [maskPreview, setMaskPreview] = useState(null);

  // ── Load validated sample (1..4) ───────────────────────────────────────
  const loadSample = async (refId) => {
    setBusy(true);
    try {
      const [imgR, metaR] = await Promise.all([
        axios.get(`${API}/phenoscan/root/sample/${refId}`, { responseType: 'blob' }),
        axios.get(`${API}/phenoscan/root/sample-meta/${refId}`),
      ]);
      const f = new File([imgR.data], metaR.data.filename || `root_ref_${refId}.png`, { type: 'image/png' });
      setImageFile(f);
      setImagePreview(URL.createObjectURL(imgR.data));
      setMode(refId === 4 ? 'minirhizotron' : 'washed');
      setGtLen(String(metaR.data.length_cm ?? ''));
      setGtTips(String(metaR.data.tips ?? ''));
      setGtBranches(String(metaR.data.branches ?? ''));
      setGtDia(String(metaR.data.diameter_cm ?? ''));
      setResult(null); setOverlay(null); setElapsedMs(null); setMaskPreview(null);
      toast.success(`Ref ${refId} loaded — GT length ${metaR.data.length_cm} cm, tips ${metaR.data.tips}, branches ${metaR.data.branches}`);
    } catch (e) {
      toast.error('Failed to load sample');
    } finally { setBusy(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImageFile(f); setImagePreview(URL.createObjectURL(f));
    setResult(null); setOverlay(null); setElapsedMs(null); setMaskPreview(null);
    setGtLen(''); setGtTips(''); setGtBranches(''); setGtDia('');
  };

  // ── Full analysis ──────────────────────────────────────────────────────
  const runAnalysis = async () => {
    if (!imageFile) return toast.error('Pick an image first');
    setBusy(true); setResult(null); setOverlay(null);
    const t0 = performance.now();
    try {
      const fd = new FormData();
      fd.append('file', imageFile);
      fd.append('mode', mode);
      fd.append('manual_threshold', String(manualThreshold));
      fd.append('adaptive_window', String(adaptiveWindow));
      fd.append('pruning_px', String(pruningPx));
      fd.append('aruco_marker_cm', String(arucoCm));
      fd.append('coin_diameter_cm', String(coinCm));
      if (gtLen) fd.append('ground_truth_length_cm', String(gtLen));
      if (gtTips) fd.append('ground_truth_tip_count', String(gtTips));
      if (gtBranches) fd.append('ground_truth_branch_count', String(gtBranches));
      if (gtDia) fd.append('ground_truth_diameter_cm', String(gtDia));
      const r = await axios.post(`${API}/phenoscan/root/analyze`, fd);
      const data = r.data.results;
      setResult(data);
      setOverlay(data.overlay_png_base64 ? `data:image/png;base64,${data.overlay_png_base64}` : null);
      setElapsedMs(Math.round(performance.now() - t0));
      toast.success('Root analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setBusy(false); }
  };

  // ── Exports ────────────────────────────────────────────────────────────
  const exportAs = async (fmt) => {
    if (!result) return;
    try {
      const r = await axios.post(`${API}/phenoscan/export?fmt=${fmt}`,
        { analysis: 'root', results: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `phenoscan_root.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const exportSkeletonSvg = () => {
    if (!result?.skeleton_svg) return toast.error('No skeleton to export');
    const blob = new Blob([result.skeleton_svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'phenoscan_root_skeleton.svg'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Skeleton SVG downloaded');
  };

  const copySettings = () => {
    navigator.clipboard?.writeText(JSON.stringify({
      module: 'phenoscan_root',
      params: { mode, manual_threshold: manualThreshold, adaptive_window: adaptiveWindow,
                pruning_px: pruningPx, aruco_marker_cm: arucoCm, coin_diameter_cm: coinCm },
      version: 'bkagrid-phenoscan-1.2',
    }, null, 2));
    toast.success('Settings copied');
  };

  // ── Hand-off trait matrix ──────────────────────────────────────────────
  const traitMatrix = useMemo(() => {
    if (!result?.skeleton) return null;
    const sk = result.skeleton || {};
    const d = result.diameter || {};
    const dd = result.depth_distribution || {};
    return {
      source: 'phenoscan_root',
      traits: {
        total_length_cm: sk.total_length_cm,
        total_length_px: sk.total_length_px,
        tip_count: sk.tip_count,
        branch_point_count: sk.branch_point_count,
        branching_frequency_per_cm: sk.branching_frequency_per_cm,
        branching_angle_mean_deg: sk.branching_angle_mean_deg,
        mean_diameter_cm: d.mean_diameter_cm,
        max_diameter_cm: d.max_diameter_cm,
        d50_diameter_cm: d.d50_diameter_cm,
        d95_diameter_cm: d.d95_diameter_cm,
        projected_area_cm2: result.area_cm2,
        volume_cm3: result.volume_cm3,
        root_depth_d50_cm: dd.d50_cm,
        root_depth_d95_cm: dd.d95_cm,
      },
    };
  }, [result]);

  const handoff = useCallback((path, target) => {
    if (!traitMatrix) return toast.error('Run an analysis first');
    navigate(path, { state: { phenoscanHandoff: { ...traitMatrix, target } } });
    toast.success('Trait matrix handed off to ' + target);
  }, [navigate, traitMatrix]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6" data-testid="root-pane">

      {/* ─── LEFT PANEL ──────────────────────────────────────────── */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>1. Image</h3>

        <div className="rounded-xl p-4 mb-3 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}
          onClick={() => fileRef.current?.click()}
          data-testid="root-upload-zone">
          {imagePreview ? (
            <img src={imagePreview} alt="Upload" style={{ maxHeight: 180, margin: '0 auto', borderRadius: 8 }} />
          ) : (
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" /> Click or drag a root image
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile}
            data-testid="root-upload-input" />
        </div>

        <div className="text-xs mb-2" style={{ color: '#4F4B44' }}>Load validated reference sample:</div>
        <div className="grid grid-cols-4 gap-1 mb-5">
          {[1, 2, 3, 4].map(ref => (
            <button key={ref} onClick={() => loadSample(ref)} disabled={busy}
              className="rounded-lg py-1.5 text-xs"
              style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
              data-testid={`root-load-sample-${ref}`}>
              <ImageIcon size={11} className="inline mr-1" /> Ref {ref}
            </button>
          ))}
        </div>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>2. Input mode</h3>
        <div className="space-y-1 mb-4">
          {INPUT_MODES.map(m => (
            <label key={m.key}
              className="flex items-start gap-2 rounded-md px-2 py-1.5 cursor-pointer"
              style={{ background: mode === m.key ? '#F3F6F3' : 'transparent',
                border: `1px solid ${mode === m.key ? '#2D6A4F' : '#E5E5E0'}` }}
              data-testid={`root-mode-${m.key}`}>
              <input type="radio" name="root-mode" value={m.key}
                checked={mode === m.key} onChange={() => setMode(m.key)} />
              <div>
                <div className="text-sm" style={{ color: '#1B4332', fontWeight: 600 }}>{m.label}</div>
                <div className="text-xs" style={{ color: '#6F695F' }}>{m.blurb}</div>
              </div>
            </label>
          ))}
        </div>

        {mode === 'manual' && (
          <label className="block mb-3 text-sm" data-testid="root-manual-threshold-wrap">
            <div className="flex justify-between mb-1" style={{ color: '#4F4B44' }}>
              <span>Threshold</span><span style={{ color: '#1B4332', fontWeight: 600 }}>{manualThreshold}</span>
            </div>
            <input type="range" min={0} max={255} value={manualThreshold}
              onChange={e => setManualThreshold(parseInt(e.target.value, 10))}
              className="w-full"
              data-testid="root-manual-threshold" />
          </label>
        )}
        {mode === 'adaptive' && (
          <label className="block mb-3 text-sm" data-testid="root-adaptive-window-wrap">
            <div className="flex justify-between mb-1" style={{ color: '#4F4B44' }}>
              <span>Sauvola window (px)</span><span style={{ color: '#1B4332', fontWeight: 600 }}>{adaptiveWindow}</span>
            </div>
            <input type="range" min={5} max={101} step={2} value={adaptiveWindow}
              onChange={e => setAdaptiveWindow(parseInt(e.target.value, 10))}
              className="w-full"
              data-testid="root-adaptive-window" />
          </label>
        )}

        <h3 className="font-display text-lg mb-3 mt-5" style={{ color: '#1B4332' }}>3. Calibration</h3>
        <div className="text-xs mb-3 rounded-md px-3 py-2"
          style={{ background: '#FFFBEB', color: '#78350F', border: '1px solid #FDE68A' }}>
          <AlertTriangle size={11} className="inline mr-1" />
          Without a marker, output will be in PIXEL units only and show an "UNCALIBRATED" banner.
        </div>
        <label className="block mb-2 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>ArUco marker side (cm)</div>
          <input type="number" step="0.1" min="0.5" value={arucoCm}
            onChange={e => setArucoCm(parseFloat(e.target.value) || 0)}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-aruco-cm" />
        </label>
        <label className="block mb-2 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Coin diameter fallback (cm)</div>
          <input type="number" step="0.1" min="0.5" value={coinCm}
            onChange={e => setCoinCm(parseFloat(e.target.value) || 0)}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }} />
        </label>
        <label className="block mb-4 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Spur pruning (px)</div>
          <input type="number" step="1" min="0" max="20" value={pruningPx}
            onChange={e => setPruningPx(parseInt(e.target.value, 10) || 0)}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-pruning-px" />
        </label>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>4. Ground truth (optional)</h3>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <input type="number" step="0.01" placeholder="length cm" value={gtLen}
            onChange={e => setGtLen(e.target.value)}
            className="rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-gt-len" />
          <input type="number" step="0.01" placeholder="diameter cm" value={gtDia}
            onChange={e => setGtDia(e.target.value)}
            className="rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-gt-dia" />
          <input type="number" step="1" placeholder="tips" value={gtTips}
            onChange={e => setGtTips(e.target.value)}
            className="rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-gt-tips" />
          <input type="number" step="1" placeholder="branches" value={gtBranches}
            onChange={e => setGtBranches(e.target.value)}
            className="rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="root-gt-branches" />
        </div>

        <button onClick={runAnalysis} disabled={busy || !imageFile}
          className="w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
          style={{ background: '#1B4332', color: '#fff' }}
          data-testid="root-run-btn">
          <Play size={14} className="inline mr-2" /> {busy ? 'Running…' : 'Run Root Analysis'}
        </button>

        <button onClick={copySettings} disabled={busy}
          className="w-full rounded-lg py-2 text-xs mt-2"
          style={{ background: '#F3F6F3', color: '#4F4B44', border: '1px solid #E5E5E0' }}
          data-testid="root-copy-settings">
          <Copy size={12} className="inline mr-2" /> Copy Settings (reproducibility)
        </button>
      </div>

      {/* ─── RIGHT PANEL ─────────────────────────────────────────── */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!result ? (
          <div className="text-center py-16">
            <GitBranch size={32} className="mx-auto mb-3" style={{ color: '#BCC5B5' }} />
            <div className="font-display text-lg" style={{ color: '#6F695F' }}>No results yet</div>
            <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
              Upload an image, or click a <b>Ref</b> button to run a validated root analysis.
            </div>
          </div>
        ) : (
          <div data-testid="root-results">
            {/* Top action bar */}
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <CalBadge cal={result.calibration} />
              {result.ground_truth && <GTOverall gt={result.ground_truth} />}
              {elapsedMs != null && (
                <span className="px-2.5 py-1 rounded-full text-xs"
                  style={{ background: '#F3F6F3', color: '#4F4B44' }}>⏱ {elapsedMs} ms</span>
              )}
              <div className="ml-auto flex gap-1">
                <ExportBtn onClick={() => exportAs('csv')} prefix="root">CSV</ExportBtn>
                <ExportBtn onClick={() => exportAs('xlsx')} prefix="root">XLSX</ExportBtn>
                <ExportBtn onClick={() => exportAs('pdf')} prefix="root">PDF</ExportBtn>
                {imageFile && <ViewHeatmapBtn file={imageFile} kind="root_depth"
                  colormap="viridis" navigate={navigate}
                  title={`Root depth · ${imageFile.name}`}
                  pixelsPerCm={result?.calibration?.pixels_per_cm}
                  testid="root-view-heatmap" />}
                <button onClick={exportSkeletonSvg}
                  className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                  style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46' }}
                  data-testid="root-export-svg">
                  <Download size={11} /> Skeleton SVG
                </button>
                {overlay && (
                  <a href={overlay} download="root_overlay.png"
                    className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                    style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#4F4B44' }}
                    data-testid="root-export-png">
                    <Download size={11} /> PNG
                  </a>
                )}
              </div>
            </div>

            {/* Ground-truth breakdown */}
            {result.ground_truth && <GTGrid gt={result.ground_truth} />}

            {/* Image panels */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <ImgPanel title="Original" src={imagePreview} />
              <ImgPanel title="Skeleton overlay (jet = diameter)" src={overlay} testid="root-overlay-img" />
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard title="Skeleton traits" rows={[
                ['Total length (cm)', result.skeleton?.total_length_cm],
                ['Total length (px)', result.skeleton?.total_length_px],
                ['Tip count', result.skeleton?.tip_count],
                ['Branch-point count', result.skeleton?.branch_point_count],
                ['Branching freq (/cm)', result.skeleton?.branching_frequency_per_cm],
                ['Branching angle mean (°)', result.skeleton?.branching_angle_mean_deg],
                ['Branching angle SD (°)', result.skeleton?.branching_angle_sd_deg],
                ['# segments', result.skeleton?.n_segments],
              ]} />
              <StatCard title="Diameter" rows={[
                ['Mean diameter (cm)', result.diameter?.mean_diameter_cm],
                ['Max diameter (cm)', result.diameter?.max_diameter_cm],
                ['D50 diameter (cm)', result.diameter?.d50_diameter_cm],
                ['D95 diameter (cm)', result.diameter?.d95_diameter_cm],
                ['Mean diameter (px)', result.diameter?.mean_diameter_px],
                ['Max diameter (px)', result.diameter?.max_diameter_px],
              ]} />
              <StatCard title="Area / Volume / Depth" rows={[
                ['Projected area (cm²)', result.area_cm2],
                ['Projected area (px)', result.area_px],
                ['Volume (cm³)', result.volume_cm3],
                ['Depth D50 (cm)', result.depth_distribution?.d50_cm],
                ['Depth D95 (cm)', result.depth_distribution?.d95_cm],
                ['Depth D50 (px)', result.depth_distribution?.d50_px],
                ['Depth D95 (px)', result.depth_distribution?.d95_px],
              ]} />
            </div>

            {/* Depth distribution bar chart */}
            {result.depth_distribution?.bins?.length > 0 && (
              <div className="rounded-xl p-4 mt-5" data-testid="root-depth-chart"
                style={{ border: '1px solid #E5E5E0', background: '#FAFBF9' }}>
                <div className="text-xs uppercase tracking-wide mb-3" style={{ color: '#4F4B44' }}>
                  Depth distribution — root length per vertical bin
                </div>
                <DepthBars bins={result.depth_distribution.bins} />
              </div>
            )}

            {/* Method / Accuracy guidance */}
            <div className="rounded-xl p-4 mt-5" data-testid="root-method-accuracy"
              style={{ border: '1px solid #E5E5E0', background: '#FFFFFF' }}>
              <div className="text-xs uppercase tracking-wide mb-3" style={{ color: '#4F4B44' }}>
                Method &amp; accuracy guidance
              </div>
              {result.method && (
                <div className="text-xs mb-3" style={{ color: '#4F4B44' }}>
                  {Object.entries(result.method).map(([k, v]) => (
                    <div key={k}><span style={{ color: '#6F695F' }}>{k}:</span> <b style={{ color: '#1B4332' }}>{String(v)}</b></div>
                  ))}
                </div>
              )}
              <div className="text-xs leading-relaxed rounded-md px-3 py-2"
                style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#1B4332' }}>
                <div className="mb-1" style={{ fontWeight: 600 }}>ACCURACY GUIDANCE</div>
                <ul className="list-disc pl-4" style={{ color: '#4F4B44' }}>
                  <li>
                    <b style={{ color: '#1B4332' }}>Roots with diameter ≥ 0.5 mm:</b> &lt; 5 % length error,
                    &lt; 8 % diameter error (tested on the RhizoVision Zenodo copper-wire dataset).
                  </li>
                  <li>
                    <b style={{ color: '#1B4332' }}>Roots with diameter &lt; 0.3 mm:</b> Nyquist-limited at
                    standard processing resolution. For fine-root research, increase scan DPI to 1200+
                    and do <u>not</u> downsample.
                  </li>
                  <li>
                    <b style={{ color: '#1B4332' }}>Synthetic reference images:</b> &lt; 2 % length error,
                    &lt; 5 % diameter error.
                  </li>
                </ul>
              </div>
            </div>

            {/* Hand-off dock */}
            <div className="rounded-xl p-4 mt-5"
              style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}
              data-testid="root-handoff">
              <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#6F695F' }}>
                Next step — hand off to BKAgriD statistics
              </div>
              <div className="flex flex-wrap gap-2">
                {HANDOFF_TARGETS.map(([label, path, key]) => (
                  <button key={key} onClick={() => handoff(path, key)}
                    data-testid={`root-handoff-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-3 py-1.5 rounded-md text-xs"
                    style={{ background: '#fff', border: '1px solid #2D6A4F', color: '#1B4332' }}>
                    {label} →
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="lg:col-span-2">
        <ValidationPanel subTab="root" subTabLabel="Root Analysis" />
      </div>
    </div>
  );
}
function CalBadge({ cal }) {
  if (!cal) return null;
  const ok = cal.calibrated;
  return (
    <span data-testid="root-cal-badge"
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#ECFDF5' : '#FEF3C7', color: ok ? '#065F46' : '#92400E' }}>
      {ok ? <Check size={11} /> : <AlertTriangle size={11} />}
      {ok ? `Calibrated · ${cal.method} · ${cal.pixels_per_cm} px/cm` : 'UNCALIBRATED — pixel units only'}
    </span>
  );
}

function GTOverall({ gt }) {
  const ok = gt.overall_status === 'OK';
  return (
    <span data-testid="root-gt-overall"
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#EFF6FF' : '#FEF3C7', color: ok ? '#1E40AF' : '#92400E' }}>
      {ok ? 'Ground-truth: all checks pass' : 'Ground-truth: partial — check table below'}
    </span>
  );
}

function GTGrid({ gt }) {
  const rows = [];
  if (gt.length) rows.push(['Length (cm)', gt.length.measured_cm, gt.length.ground_truth_cm, gt.length.rel_error_pct + '%', gt.length.status]);
  if (gt.tips) rows.push(['Tip count', gt.tips.measured, gt.tips.ground_truth, '±' + gt.tips.diff, gt.tips.status]);
  if (gt.branches) rows.push(['Branch count', gt.branches.measured, gt.branches.ground_truth, '±' + gt.branches.diff, gt.branches.status]);
  if (gt.diameter) rows.push(['Mean diameter (cm)', gt.diameter.measured_cm, gt.diameter.ground_truth_cm, gt.diameter.rel_error_pct + '%', gt.diameter.status]);
  if (!rows.length) return null;
  return (
    <div data-testid="root-gt-box" className="rounded-xl p-3 mb-4"
      style={{ background: gt.overall_status === 'OK' ? '#F0FDF4' : '#FFFBEB',
        border: `1px solid ${gt.overall_status === 'OK' ? '#BBF7D0' : '#FDE68A'}` }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ color: '#6F695F', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <th className="text-left pb-1">Metric</th>
            <th className="text-right pb-1">Measured</th>
            <th className="text-right pb-1">GT</th>
            <th className="text-right pb-1">Δ</th>
            <th className="text-right pb-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, m, g, d, st], i) => (
            <tr key={i} style={{ borderTop: '1px dashed #F0EFEA' }}>
              <td style={{ color: '#1B4332', padding: '4px 0' }}>{name}</td>
              <td style={{ textAlign: 'right', color: '#1B4332', fontVariantNumeric: 'tabular-nums' }}>{m}</td>
              <td style={{ textAlign: 'right', color: '#4F4B44', fontVariantNumeric: 'tabular-nums' }}>{g}</td>
              <td style={{ textAlign: 'right', color: st === 'OK' ? '#065F46' : '#92400E', fontVariantNumeric: 'tabular-nums' }}>{d}</td>
              <td style={{ textAlign: 'right', color: st === 'OK' ? '#065F46' : '#92400E' }}>{st === 'OK' ? '✓' : '⚠'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImgPanel({ title, src, testid }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
      <div className="text-xs px-3 py-1.5" style={{ background: '#F3F6F3', color: '#4F4B44' }}>{title}</div>
      <div className="bg-white flex items-center justify-center" style={{ height: 280 }}>
        {src ? <img data-testid={testid} src={src} alt={title} style={{ maxHeight: 280, maxWidth: '100%' }} /> :
          <div className="text-sm" style={{ color: '#BCC5B5' }}>—</div>}
      </div>
    </div>
  );
}

function DepthBars({ bins }) {
  const max = Math.max(1, ...bins.map(b => b.length_pct));
  return (
    <div className="flex items-end gap-1" style={{ height: 140 }}>
      {bins.map((b, i) => {
        const pct = b.length_pct || 0;
        const h = (pct / max) * 120;
        const label = b.depth_end_cm != null ? `${b.depth_start_cm}–${b.depth_end_cm} cm` :
                      `${Math.round(b.depth_start_px)}–${Math.round(b.depth_end_px)} px`;
        return (
          <div key={i} title={`${label} · ${pct}%`} className="flex-1 flex flex-col items-center justify-end">
            <div style={{ width: '100%', height: h, background: '#2D6A4F', borderRadius: '3px 3px 0 0' }} />
            <div className="text-[10px] mt-1" style={{ color: '#6F695F' }}>{pct}%</div>
          </div>
        );
      })}
    </div>
  );
}
